import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import {
  Network,
  AlertTriangle,
  HardHat,
  Map,
  Radio,
  Brain,
  Building2,
  Navigation,
  Construction,
  Users,
  Route,
  CloudRain,
  Database,
  TrendingDown,
  Gauge,
  Cpu,
  Handshake,
} from "lucide-react";
import {
  NA_PRIMARY,
  NA_SECONDARY,
  NA_ACCENT,
  naDarkCardBase,
  naReadableText,
  naMutedText,
  naGradientLine,
  onDarkCardEnter,
  onDarkCardLeave,
} from "./naSectionTokens";

const foundations = [
  { label: "BIM & Digital Engineering", icon: HardHat, color: NA_PRIMARY },
  { label: "GIS & Spatial Intelligence", icon: Map, color: NA_SECONDARY },
  { label: "IoT & Real-Time Infrastructure Monitoring", icon: Radio, color: NA_PRIMARY },
  { label: "AI & Predictive Analytics", icon: Brain, color: NA_SECONDARY },
  { label: "Smart City Operations", icon: Building2, color: NA_PRIMARY },
  { label: "Transportation & Mobility Systems", icon: Navigation, color: NA_SECONDARY },
];

const challenges = [
  { label: "Aging infrastructure networks", icon: Construction, color: NA_ACCENT },
  { label: "Rising urban population pressures", icon: Users, color: NA_PRIMARY },
  { label: "Increasing transportation demand", icon: Route, color: NA_SECONDARY },
  { label: "Climate resilience and sustainability targets", icon: CloudRain, color: NA_ACCENT },
  { label: "Fragmented infrastructure data systems", icon: Database, color: NA_PRIMARY },
  { label: "Cost overruns in major projects", icon: TrendingDown, color: NA_SECONDARY },
  {
    label: "Need for predictive maintenance and operational intelligence",
    icon: Gauge,
    color: NA_ACCENT,
  },
  {
    label: "Pressure to modernize public infrastructure through AI and digital technologies",
    icon: Cpu,
    color: NA_PRIMARY,
  },
];

function SectionHeader({ title, subtitle, icon: Icon, accent, size }) {
  return (
    <div className="mb-3">
      <div className="d-flex align-items-center mb-1" style={{ gap: "0.6rem" }}>
        <div
          style={{
            width: size ? 40 : 36,
            height: size ? 40 : 36,
            minWidth: size ? 40 : 36,
            borderRadius: "10px",
            background: `linear-gradient(145deg, ${accent}35, ${accent}12)`,
            border: `2px solid ${accent}55`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${accent}28`,
          }}
        >
          <Icon size={size ? 20 : 18} strokeWidth={2.2} style={{ color: accent }} />
        </div>
        <div>
          <h3
            className="mb-0"
            style={{
              fontSize: size ? "1.15rem" : "1rem",
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: accent,
              ...naReadableText,
            }}
          >
            {title}
          </h3>
          {subtitle && (
            <p className="mb-0 mt-0" style={{ fontSize: size ? "0.82rem" : "0.78rem", ...naMutedText }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <div style={{ width: "72px", height: "4px", borderRadius: "2px", ...naGradientLine }} />
    </div>
  );
}

function TopicTile({ item, size, compact }) {
  const Icon = item.icon;
  const [hovered, setHovered] = React.useState(false);
  const iconBox = size ? 30 : 28;
  const iconSize = size ? 15 : 14;

  return (
    <Col xs={12} sm={6} lg={compact ? 6 : 4} className="mb-2 px-2">
      <Card
        onMouseEnter={(e) => {
          setHovered(true);
          e.currentTarget.style.background = "rgba(0, 0, 0, 0.88)";
          e.currentTarget.style.borderColor = `${item.color}55`;
          e.currentTarget.style.borderLeftColor = item.color;
        }}
        onMouseLeave={(e) => {
          setHovered(false);
          onDarkCardLeave(e);
          e.currentTarget.style.borderLeftColor = "transparent";
        }}
        style={{
          ...naDarkCardBase,
          borderRadius: "10px",
          padding: size ? "0.45rem 0.55rem" : "0.4rem 0.5rem",
          margin: 0,
          height: "100%",
          transition: "all 0.25s ease",
          cursor: "pointer",
          borderLeft: `2px solid ${hovered ? item.color : "transparent"}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div
            style={{
              width: iconBox,
              height: iconBox,
              minWidth: iconBox,
              borderRadius: "8px",
              background: `${item.color}18`,
              border: `1px solid ${item.color}${hovered ? "66" : "33"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={iconSize} strokeWidth={2.25} style={{ color: item.color }} />
          </div>
          <span
            style={{
              fontSize: size ? (compact ? "0.8rem" : "0.82rem") : "0.76rem",
              lineHeight: 1.3,
              fontWeight: 600,
              flex: 1,
              ...naReadableText,
            }}
          >
            {item.label}
          </span>
        </div>
      </Card>
    </Col>
  );
}

function NAWhyItMattersSection() {
  const size =
    document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

  return (
    <section className="section py-5" style={{ background: "transparent", position: "relative", zIndex: 1 }}>
      <Container>
        <Row className="mb-4">
          <Col lg={10}>
            <h2
              className="mb-3"
              style={{
                fontSize: size ? "2.2rem" : "1.65rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: NA_PRIMARY,
                ...naReadableText,
              }}
            >
              Why this event matters
            </h2>
            <p
              className="mb-4"
              style={{
                fontSize: size ? "1.15rem" : "1.02rem",
                lineHeight: 1.8,
                ...naReadableText,
              }}
            >
              North America is in a major phase of infrastructure modernization and digital
              transformation. Governments, developers, airports, utilities, and engineering firms
              are rapidly investing in technologies that improve operational efficiency,
              sustainability, resilience, and lifecycle asset management.
            </p>
            <p
              className="mb-0"
              style={{
                fontSize: size ? "1.05rem" : "0.98rem",
                lineHeight: 1.7,
                fontWeight: 600,
                color: NA_SECONDARY,
                ...naReadableText,
              }}
            >
              Digital twins are becoming the foundation that connects:
            </p>
          </Col>
        </Row>

        {/* Integrations */}
        <Row className="mb-2">
          <Col lg={12}>
            <SectionHeader
              title="Integrations"
              subtitle="The connected stack powering digital twin ecosystems"
              icon={Network}
              accent={NA_PRIMARY}
              size={size}
            />
          </Col>
        </Row>
        <Row className="mb-3 mx-n2">
          {foundations.map((item, i) => (
            <TopicTile key={i} item={item} size={size} compact={false} />
          ))}
        </Row>

        <Row className="mb-4">
          <Col lg={12}>
            <Card
              style={{
                ...naDarkCardBase,
                borderRadius: "16px",
                padding: size ? "1.5rem 1.75rem" : "1.25rem 1.35rem",
                border: `1px solid ${NA_SECONDARY}44`,
              }}
              onMouseEnter={(e) => onDarkCardEnter(e, NA_SECONDARY)}
              onMouseLeave={onDarkCardLeave}
            >
              <div className="d-flex align-items-start" style={{ gap: "1rem" }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    minWidth: 48,
                    borderRadius: "12px",
                    background: `linear-gradient(145deg, ${NA_SECONDARY}30, ${NA_SECONDARY}10)`,
                    border: `2px solid ${NA_SECONDARY}55`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Handshake size={24} strokeWidth={2} style={{ color: NA_SECONDARY }} />
                </div>
                <p
                  className="mb-0"
                  style={{
                    fontSize: size ? "1.05rem" : "0.98rem",
                    lineHeight: 1.8,
                    ...naReadableText,
                  }}
                >
                  As organizations move beyond pilot projects into real-world implementation, there
                  is an urgent need for collaboration, interoperability standards, case studies, and
                  strategic partnerships.
                </p>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Pressure points */}
        <Row className="mb-2">
          <Col lg={12}>
            <h3
              className="mb-2"
              style={{
                fontSize: size ? "1.5rem" : "1.25rem",
                fontWeight: 700,
                color: NA_SECONDARY,
                ...naReadableText,
              }}
            >
              Industry challenges driving adoption
            </h3>
            <SectionHeader
              title="Pressure points"
              subtitle="Cities & infrastructure agencies are facing"
              icon={AlertTriangle}
              accent={NA_ACCENT}
              size={size}
            />
          </Col>
        </Row>
        <Row className="mb-3 mx-n2">
          {challenges.map((item, i) => (
            <TopicTile key={i} item={item} size={size} compact />
          ))}
        </Row>

        <Row>
          <Col>
            <Card
              style={{
                ...naDarkCardBase,
                borderRadius: "14px",
                padding: size ? "1.35rem 1.5rem" : "1.15rem 1.25rem",
                borderTop: `4px solid ${NA_PRIMARY}`,
              }}
            >
              <p
                className="mb-0"
                style={{
                  fontSize: size ? "1.08rem" : "1rem",
                  lineHeight: 1.75,
                  fontWeight: 500,
                  ...naReadableText,
                }}
              >
                Digital twins provide a scalable solution by enabling organizations to simulate,
                monitor, optimize, and manage infrastructure assets in real time.
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default NAWhyItMattersSection;
